import { isAxiosError } from 'axios';

import { ProblemSlug, type components } from '@/shared/api/generated-types';
export { ProblemSlug };

/**
 * Forward-compat slug type. Known slugs autocomplete and narrow; unknown
 * slugs from a newer backend still type-check so the runtime fallback chain
 * (`messageForCode → detail → title → caller default`) can degrade gracefully
 * instead of erroring.
 */
export type ProblemCode = ProblemSlug | (string & {});

/**
 * RFC 7807 `application/problem+json` shape returned by the Extract API — a flat
 * view over every problem variant so {@link ApiError} can read one shape. Each
 * variant carries its own fields on the OpenAPI schema (`ExtractionFailedProblemDetails`,
 * `HazcheckValidationProblemDetails`), so every field here is codegen-backed; the
 * guard below fails the build if the backend adds one this type doesn't model.
 * `type` keeps the {@link ProblemCode} slug type and the core fields stay required,
 * which the permissive generated schemas don't express.
 */
export interface ProblemDocument {
  type: ProblemCode;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
  traceId?: string;
  helpUrl?: string;
  errors?: Record<string, string[]>;
  errorCategory?: string | null;
  transactionId?: string | null;
  jobId?: string | null;
  cargoIndices?: Record<string, number>;
}

type StripIndex<T> = { [K in keyof T as string extends K ? never : K]: T[K] };
type GeneratedProblemFields = StripIndex<
  components['schemas']['ProblemDetails'] &
    components['schemas']['ExtractionFailedProblemDetails'] &
    components['schemas']['HazcheckValidationProblemDetails']
>;
type AssertTrue<T extends true> = T;
type _ProblemDocumentCoversSchema = AssertTrue<
  Exclude<keyof GeneratedProblemFields, keyof ProblemDocument> extends never ? true : false
>;

/**
 * Thrown for any non-2xx HTTP response that carries a problem+json body.
 * Consumers `switch` on {@link code} to react to specific failures rather
 * than parsing {@link message}. {@link errors} carries field-level detail
 * for validation failures.
 */
export class ApiError extends Error {
  readonly code: ProblemCode;
  readonly status: number;
  readonly detail?: string;
  readonly traceId?: string;
  readonly helpUrl?: string;
  readonly errors?: Record<string, string[]>;
  readonly errorCategory?: string | null;
  readonly transactionId?: string | null;
  readonly jobId?: string | null;
  readonly cargoIndices?: Record<string, number>;
  /** URL/method of the failed request — from the axios config the interceptor
   *  otherwise discards; support reports relay them to the service desk. */
  readonly requestUrl?: string;
  readonly requestMethod?: string;

  constructor(problem: ProblemDocument, request?: { url?: string; method?: string }) {
    super(problem.detail || problem.title || 'Request failed');
    this.name = 'ApiError';
    this.code = problem.type;
    this.status = problem.status;
    this.detail = problem.detail;
    this.traceId = problem.traceId;
    this.helpUrl = problem.helpUrl;
    this.errors = problem.errors;
    this.errorCategory = problem.errorCategory;
    this.transactionId = problem.transactionId;
    this.jobId = problem.jobId;
    this.cargoIndices = problem.cargoIndices;
    this.requestUrl = request?.url;
    this.requestMethod = request?.method;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * HTTP status of a caught error, whether it surfaced as an {@link ApiError}
 * (problem+json body) or a raw {@link isAxiosError} (no body reached us).
 * `undefined` means no HTTP response at all — network down, CORS, DNS.
 */
export function getHttpStatus(error: unknown): number | undefined {
  if (isApiError(error)) return error.status;
  if (isAxiosError(error)) return error.response?.status;
  return undefined;
}

export function isProblemDocument(data: unknown): data is ProblemDocument {
  if (typeof data !== 'object' || data === null) return false;
  const obj = data as Record<string, unknown>;
  return (
    typeof obj.type === 'string' && typeof obj.title === 'string' && typeof obj.status === 'number'
  );
}
